import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Heart,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  Quote,
  Infinity as InfinityIcon,
  Share2,
  Download,
  X,
  Loader2,
  Type,
  BookOpen,
  ZoomIn,
  ZoomOut,
  Feather,
  Layers,
  Search,
} from 'lucide-react';
import { CoupleSettings, TimeElapsed } from '../types';
import { romanticAudio } from '../utils/audio';
import {
  RomanticPhrase,
  PhraseMode,
  RomanticCategory,
  RomanticType,
  getRandomInfinitePhrase,
} from '../utils/infinitePhrases';
import {
  generateNoteCardImage,
  NoteImageResult,
  NoteFontFamily,
} from '../utils/generateNoteImage';
import {
  ALL_ROMANTIC_FONTS,
  FONTS_BY_ID,
  FONT_CATEGORY_TABS,
  FontCategory,
  RomanticFontItem,
  ensureGoogleFontLoaded,
} from '../utils/romanticFontsCatalog';

interface RomanticAnimationsViewProps {
  settings: CoupleSettings;
  timeElapsed: TimeElapsed;
  onGoToGarden?: () => void;
  onGoToBlog?: () => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  decay: number;
  rotation: number;
  vRot: number;
  type: 'heart' | 'petal' | 'sparkle' | 'star';
}

export const RomanticAnimationsView: React.FC<RomanticAnimationsViewProps> = ({
  settings,
}) => {
  const [phraseMode, setPhraseMode] = useState<PhraseMode>('with-author');
  const [currentPhrase, setCurrentPhrase] = useState<RomanticPhrase>(() =>
    getRandomInfinitePhrase('with-author')
  );
  const [copiedQuote, setCopiedQuote] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [exploredCount, setExploredCount] = useState<number>(1);

  // Note Card Generation State
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [noteResult, setNoteResult] = useState<NoteImageResult | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [sharedSuccess, setSharedSuccess] = useState(false);
  const [shareFeedbackMsg, setShareFeedbackMsg] = useState<string | null>(null);

  // Font Selection State (+120 Tipografías)
  const [selectedFont, setSelectedFont] = useState<NoteFontFamily>('playfair');
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [fontSearchQuery, setFontSearchQuery] = useState('');
  const [selectedFontCategory, setSelectedFontCategory] = useState<FontCategory>('all');
  const [isUpdatingFont, setIsUpdatingFont] = useState(false);
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animFrameIdRef = useRef<number | null>(null);

  const p1 = settings.partner1Name || 'Jackson';
  const p2 = settings.partner2Name || 'Laura';

  // Objeto de fuente activa
  const activeFontItem = useMemo(() => {
    return FONTS_BY_ID[selectedFont] || FONTS_BY_ID['playfair'] || ALL_ROMANTIC_FONTS[0];
  }, [selectedFont]);

  // Carga reactiva de la fuente seleccionada
  useEffect(() => {
    ensureGoogleFontLoaded(selectedFont);
  }, [selectedFont]);

  // Filtrado reactivo de tipografías por categoría y búsqueda
  const filteredFonts = useMemo(() => {
    const q = fontSearchQuery.trim().toLowerCase();
    return ALL_ROMANTIC_FONTS.filter((f) => {
      const matchCat =
        selectedFontCategory === 'all' || f.category === selectedFontCategory;
      const matchQuery =
        !q ||
        f.name.toLowerCase().includes(q) ||
        f.categoryLabel.toLowerCase().includes(q) ||
        f.category.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [selectedFontCategory, fontSearchQuery]);

  // Pre-cargar fuentes del lote visible cuando el selector esté abierto
  useEffect(() => {
    if (showFontSelector) {
      filteredFonts.slice(0, 32).forEach((f) => {
        ensureGoogleFontLoaded(f.id);
      });
    }
  }, [showFontSelector, filteredFonts]);

  // Typography class for on-card phrase preview
  const getCardFontClass = (font: RomanticFontItem) => {
    switch (font.category) {
      case 'pixel':
        return "text-sm sm:text-base md:text-lg tracking-wider leading-relaxed text-stone-900";
      case 'cuadrada':
        return "text-base sm:text-lg md:text-xl tracking-wider leading-relaxed text-stone-900 font-semibold";
      case 'manuscrita':
        return "text-xl sm:text-2xl md:text-3xl tracking-wide leading-relaxed text-stone-900";
      case 'cursiva':
        return "text-xl sm:text-2xl md:text-3xl tracking-wide leading-relaxed text-stone-900";
      case 'elegante':
        return "text-lg sm:text-xl md:text-2xl leading-relaxed text-stone-900";
      case 'poetica':
        return "text-xl sm:text-2xl md:text-[26px] leading-relaxed text-stone-900";
      case 'vintage':
        return "text-sm sm:text-base md:text-lg tracking-normal leading-relaxed text-stone-800";
      case 'decorativa':
        return "text-xl sm:text-2xl md:text-3xl tracking-wide leading-relaxed text-stone-900";
      case 'moderna':
      default:
        return "text-base sm:text-lg md:text-xl tracking-normal leading-relaxed text-stone-900";
    }
  };

  // Burst animation for touch and refresh
  const createBurstAt = (x: number, y: number, count = 18) => {
    const colors = ['#fb7185', '#f43f5e', '#fda4af', '#f59e0b', '#fb923c', '#ec4899', '#be123c'];

    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 1.8 + Math.random() * 4.2;
      const types: Particle['type'][] = ['heart', 'petal', 'sparkle', 'star'];
      const type = types[Math.floor(Math.random() * types.length)];

      newParticles.push({
        id: Date.now() + Math.random(),
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.2,
        size: 9 + Math.random() * 14,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: 1,
        decay: 0.014 + Math.random() * 0.016,
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 0.1,
        type,
      });
    }

    particlesRef.current.push(...newParticles);
    romanticAudio.playChime();
  };

  // Canvas render loop for ambient petals & floating love particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // Initialize floating ambient particles
    const initAmbientParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < 22; i++) {
        particlesRef.current.push({
          id: Math.random(),
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.4,
          vy: 0.25 + Math.random() * 0.6,
          size: 7 + Math.random() * 11,
          color: ['#fda4af', '#f43f5e', '#fb7185', '#fbcfe8', '#fecdd3'][Math.floor(Math.random() * 5)],
          alpha: 0.25 + Math.random() * 0.35,
          decay: 0,
          rotation: Math.random() * Math.PI * 2,
          vRot: (Math.random() - 0.5) * 0.02,
          type: Math.random() > 0.45 ? 'petal' : 'heart',
        });
      }
    };
    initAmbientParticles();

    const render = () => {
      if (document.hidden) {
        animFrameIdRef.current = requestAnimationFrame(render);
        return;
      }
      ctx.clearRect(0, 0, width, height);

      const particles = particlesRef.current;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.vRot;

        if (p.decay === 0) {
          if (p.y > height + 20) p.y = -20;
          if (p.x > width + 20) p.x = -20;
          if (p.x < -20) p.x = width + 20;
        } else {
          p.vy += 0.04;
          p.alpha -= p.decay;
          if (p.alpha <= 0) {
            particles.splice(i, 1);
            continue;
          }
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.alpha);

        if (p.type === 'heart') {
          ctx.fillStyle = p.color;
          const s = p.size;
          ctx.beginPath();
          ctx.moveTo(0, s * 0.3);
          ctx.bezierCurveTo(-s * 0.5, -s * 0.3, -s, s * 0.2, 0, s);
          ctx.bezierCurveTo(s, s * 0.2, s * 0.5, -s * 0.3, 0, s * 0.3);
          ctx.fill();
        } else if (p.type === 'petal') {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size * 0.38, p.size * 0.75, Math.PI / 4, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(0, 0, p.size * 0.25, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }

      animFrameIdRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameIdRef.current) {
        cancelAnimationFrame(animFrameIdRef.current);
      }
    };
  }, []);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    createBurstAt(x, y, 22);
  };

  const handleSwitchMode = (newMode: PhraseMode) => {
    if (newMode === phraseMode && !isRefreshing) return;
    setPhraseMode(newMode);
    setIsRefreshing(true);
    romanticAudio.playChime();

    const canvas = canvasRef.current;
    if (canvas) {
      createBurstAt(canvas.offsetWidth / 2, canvas.offsetHeight * 0.42, 18);
    }

    const nextPhrase = getRandomInfinitePhrase(newMode);
    setCurrentPhrase(nextPhrase);
    setExploredCount((prev) => prev + 1);

    setTimeout(() => {
      setIsRefreshing(false);
    }, 280);
  };

  const handleNextQuote = () => {
    setIsRefreshing(true);
    romanticAudio.playBloomSuccess();

    const canvas = canvasRef.current;
    if (canvas) {
      createBurstAt(canvas.offsetWidth / 2, canvas.offsetHeight * 0.42, 18);
    }

    const nextPhrase = getRandomInfinitePhrase(phraseMode);
    setCurrentPhrase(nextPhrase);
    setExploredCount((prev) => prev + 1);

    setTimeout(() => {
      setIsRefreshing(false);
    }, 280);
  };

  const handleCopyQuote = () => {
    if (navigator.clipboard) {
      const textToShare = currentPhrase.author
        ? `"${currentPhrase.text}"\n— ${currentPhrase.author}`
        : `"${currentPhrase.text}"`;
      navigator.clipboard.writeText(textToShare);
      setCopiedQuote(true);
      romanticAudio.playChime();
      setTimeout(() => setCopiedQuote(false), 2200);
    }
  };

  const handleOpenShareNote = async (fontToUse: NoteFontFamily = selectedFont) => {
    try {
      setIsGeneratingNote(true);
      setIsPreviewZoomed(false);
      romanticAudio.playChime();

      const result = await generateNoteCardImage(currentPhrase, fontToUse, exploredCount);
      setNoteResult(result);
      setShowNoteModal(true);
    } catch (err) {
      console.error('Error generando nota estilo cuaderno:', err);
      handleCopyQuote();
    } finally {
      setIsGeneratingNote(false);
    }
  };

  const handleChangeFontInModal = async (newFont: NoteFontFamily) => {
    if (newFont === selectedFont && noteResult) return;
    setSelectedFont(newFont);
    try {
      setIsUpdatingFont(true);
      const result = await generateNoteCardImage(currentPhrase, newFont, exploredCount);
      setNoteResult(result);
      romanticAudio.playChime();
    } catch (err) {
      console.error('Error cambiando fuente en la nota:', err);
    } finally {
      setIsUpdatingFont(false);
    }
  };

  const handleDownloadImage = () => {
    if (!noteResult) return;
    romanticAudio.playBloomSuccess();

    try {
      const objectUrl = URL.createObjectURL(noteResult.blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = noteResult.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

      setSharedSuccess(true);
      setShareFeedbackMsg('¡Postal descargada con éxito!');
      setTimeout(() => {
        setSharedSuccess(false);
        setShareFeedbackMsg(null);
      }, 3000);
    } catch (e) {
      console.error('Error durante la descarga:', e);
    }
  };

  const handleNativeShare = async () => {
    if (!noteResult) return;

    if (navigator.share && navigator.canShare && noteResult.file) {
      const shareData = {
        files: [noteResult.file],
        title: 'Frase para ti',
        text: `"${currentPhrase.text}" ${currentPhrase.author ? `— ${currentPhrase.author}` : ''}`,
      };

      if (navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          setSharedSuccess(true);
          setShareFeedbackMsg('¡Compartido con éxito!');
          romanticAudio.playBloomSuccess();
          setTimeout(() => {
            setSharedSuccess(false);
            setShareFeedbackMsg(null);
          }, 3000);
          return;
        } catch (err) {
          const error = err as { name?: string };
          if (error?.name === 'AbortError') return;
          console.warn('Web Share API falló, usando descarga:', err);
        }
      }
    }
    handleDownloadImage();
  };

  return (
    <div
      className="relative min-h-[calc(100vh-4rem)] overflow-hidden select-none font-['Plus_Jakarta_Sans',sans-serif] text-stone-900 flex flex-col justify-start items-center py-6 sm:py-10 px-4"
      id="romantic-phrases-view"
    >
      {/* Interactive Ambient Canvas */}
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        className="absolute inset-0 w-full h-full cursor-crosshair z-0"
        title="Toca o haz clic para crear destellos de amor"
      />

      {/* Ambient background light gradients */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_20%,rgba(244,63,94,0.06),transparent_65%)]" />

      {/* Main Container */}
      <div className="relative z-10 max-w-4xl w-full mx-auto flex flex-col items-center text-center space-y-6 sm:space-y-8">
        {/* ======================================================== */}
        {/* 1. HERO EXHIBITION BANNER */}
        {/* ======================================================== */}
        <div className="w-full rounded-[2.5rem] overflow-hidden p-6 sm:p-10 border border-[#F0DDE4] bg-white shadow-[0_12px_36px_rgba(232,93,142,0.08)] text-center sm:text-left flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-xl mx-auto sm:mx-0">
            {/* Tag: Frases */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] text-[11px] font-bold tracking-wider uppercase shadow-2xs mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#E83E78] animate-pulse" />
              <span>Frases y Dedicatorias</span>
            </div>

            {/* Display Headline */}
            <h1 className="font-['Playfair_Display',serif] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#302B30] tracking-tight leading-tight">
              Frases para Dedicar
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base text-[#77717A] font-medium mt-3 leading-relaxed">
              Inspiración poética, versos célebres y dedicatorias de amor listas para compartir y atesorar.
            </p>
          </div>

          {/* Controls: Mode Switcher (Con Autor / Sin Autor) */}
          <div className="flex flex-col items-center sm:items-end gap-3 self-center lg:self-end">
            <div className="inline-flex items-center p-1.5 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] shadow-2xs">
              <button
                type="button"
                onClick={() => handleSwitchMode('with-author')}
                className={`px-4 py-2 rounded-xl text-xs sm:text-[13px] font-bold transition-all cursor-pointer ${
                  phraseMode === 'with-author'
                    ? 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white shadow-md shadow-pink-500/25'
                    : 'text-[#77717A] hover:text-[#302B30] hover:bg-white'
                }`}
              >
                Con Autor
              </button>
              <button
                type="button"
                onClick={() => handleSwitchMode('without-author')}
                className={`px-4 py-2 rounded-xl text-xs sm:text-[13px] font-bold transition-all cursor-pointer ${
                  phraseMode === 'without-author'
                    ? 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white shadow-md shadow-pink-500/25'
                    : 'text-[#77717A] hover:text-[#302B30] hover:bg-white'
                }`}
              >
                Sin Autor
              </button>
            </div>

            {/* Explored count badge */}
            <div className="inline-flex items-center gap-1.5 text-xs text-[#77717A] font-medium">
              <InfinityIcon className="w-3.5 h-3.5 text-[#E83E78]" />
              <span>Exploradas sin repetir:</span>
              <strong className="text-[#302B30] font-mono font-bold">#{exploredCount}</strong>
            </div>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. CENTRAL POETRY SHOWCASE CARD */}
        {/* ======================================================== */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPhrase.text}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -14, scale: 0.98 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="w-full max-w-2xl p-7 sm:p-12 rounded-[2.5rem] bg-white border border-[#F0DDE4] shadow-[0_20px_50px_-15px_rgba(232,93,142,0.1)] relative overflow-hidden flex flex-col items-center"
          >
            {/* Top delicate ribbon bar with Type Badge (Frase, Poesía, Poema) */}
            <div className="w-full flex items-center justify-between pb-4 border-b border-[#F0DDE4] gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  currentPhrase.type === 'Poema'
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : currentPhrase.type === 'Poesía'
                    ? 'bg-purple-50 border-purple-200 text-purple-900'
                    : 'bg-[#FFF4F7] border-[#F0DDE4] text-[#E83E78]'
                }`}
              >
                <Sparkles className="w-3 h-3 text-[#E83E78]" />
                <span>{currentPhrase.type}</span>
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowFontSelector((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    showFontSelector
                      ? 'bg-[#FFE8F0] text-[#E83E78] border border-[#E85D8E]'
                      : 'bg-white hover:bg-[#FFF4F7] text-[#302B30] border border-[#F0DDE4]'
                  }`}
                  title="Cambiar tipografía de la frase"
                >
                  <Type className="w-3.5 h-3.5 text-[#E83E78]" />
                  <span>Tipografía</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenShareNote(selectedFont)}
                  disabled={isGeneratingNote}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs transition-all cursor-pointer shadow-md shadow-pink-500/25"
                  title="Generar postal estilo cuaderno"
                >
                  {isGeneratingNote ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                  ) : (
                    <Share2 className="w-3.5 h-3.5 text-white" />
                  )}
                  <span>Crear Postal</span>
                </button>
              </div>
            </div>

            {/* Font selector dropdown - Catálogo Maestro de +120 Tipografías */}
            <AnimatePresence>
              {showFontSelector && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="w-full pt-3 pb-2 overflow-hidden text-left"
                >
                  <div className="p-3.5 sm:p-5 rounded-3xl bg-stone-50/95 border border-stone-200/90 shadow-inner flex flex-col space-y-3">
                    {/* Header: Title + Active font indicator */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Type className="w-4 h-4 text-rose-600" />
                        <span className="text-xs font-bold text-stone-900">
                          Catálogo de Tipografías
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-100 text-rose-800">
                          {ALL_ROMANTIC_FONTS.length} estilos
                        </span>
                      </div>
                      <div className="text-[11px] text-stone-600 truncate max-w-full">
                        Activa:{' '}
                        <span className="font-bold text-rose-700 bg-white px-2 py-0.5 rounded-lg border border-rose-200 shadow-2xs">
                          {activeFontItem.name} ({activeFontItem.categoryLabel})
                        </span>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div className="relative w-full">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                      <input
                        type="text"
                        value={fontSearchQuery}
                        onChange={(e) => setFontSearchQuery(e.target.value)}
                        placeholder="Buscar por nombre o estilo (ej. Great Vibes, Vintage, Parisienne...)"
                        className="w-full pl-9 pr-8 py-2 text-xs rounded-2xl bg-white border border-stone-200 focus:outline-none focus:ring-2 focus:ring-rose-400 text-stone-800 placeholder:text-stone-400 transition-all shadow-2xs"
                      />
                      {fontSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setFontSearchQuery('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 p-0.5 rounded-full cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Category tabs */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar text-xs">
                      {FONT_CATEGORY_TABS.map((cat) => {
                        const isCatActive = selectedFontCategory === cat.id;
                        const count =
                          cat.id === 'all'
                            ? ALL_ROMANTIC_FONTS.length
                            : ALL_ROMANTIC_FONTS.filter((f) => f.category === cat.id).length;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              setSelectedFontCategory(cat.id);
                              romanticAudio.playChime();
                            }}
                            className={`whitespace-nowrap px-2.5 py-1 rounded-xl font-bold text-[11px] transition-all cursor-pointer flex items-center gap-1 shrink-0 ${
                              isCatActive
                                ? 'bg-stone-900 text-white shadow-2xs'
                                : 'bg-white hover:bg-stone-200/70 text-stone-600 border border-stone-200/80'
                            }`}
                          >
                            <span>{cat.label}</span>
                            <span
                              className={`text-[9px] px-1.5 py-0.2 rounded-full ${
                                isCatActive
                                  ? 'bg-stone-700 text-stone-200'
                                  : 'bg-stone-100 text-stone-500'
                              }`}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Font Cards Grid with Live Previews */}
                    <div className="max-h-64 sm:max-h-72 overflow-y-auto pr-1 custom-scrollbar grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-1">
                      {filteredFonts.map((font) => {
                        const isSelected = selectedFont === font.id;
                        return (
                          <button
                            key={font.id}
                            type="button"
                            onMouseEnter={() => ensureGoogleFontLoaded(font.id)}
                            onClick={() => {
                              setSelectedFont(font.id);
                              ensureGoogleFontLoaded(font.id);
                              romanticAudio.playChime();
                            }}
                            className={`group p-2.5 rounded-2xl border text-left flex flex-col justify-between transition-all cursor-pointer relative overflow-hidden ${
                              isSelected
                                ? 'border-rose-500 bg-rose-50/90 text-stone-900 ring-2 ring-rose-400/50 shadow-xs'
                                : 'border-stone-200/90 bg-white hover:bg-stone-100/60 hover:border-rose-200 text-stone-800'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-1 w-full mb-1">
                              <span className="text-[11px] font-bold truncate text-stone-800 group-hover:text-rose-600">
                                {font.name}
                              </span>
                              {isSelected ? (
                                <span className="w-3.5 h-3.5 rounded-full bg-rose-600 text-white flex items-center justify-center shrink-0">
                                  <Check className="w-2 h-2" />
                                </span>
                              ) : (
                                <span className="text-[9px] text-stone-400 font-medium truncate shrink-0">
                                  {font.categoryLabel}
                                </span>
                              )}
                            </div>

                            {/* Live sample text rendered in this exact font */}
                            <div className="py-0.5 overflow-hidden">
                              <span
                                style={{
                                  fontFamily: font.cssFamily,
                                  fontStyle: font.style,
                                  fontWeight: font.weight,
                                }}
                                className="block text-base sm:text-lg leading-tight truncate text-stone-900"
                              >
                                {font.sampleText || 'Amor Eterno'}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Footer count indicator */}
                    <div className="pt-1 flex items-center justify-between text-[10px] text-stone-500 border-t border-stone-200/60">
                      <span>
                        Mostrando {filteredFonts.length} de {ALL_ROMANTIC_FONTS.length} tipografías
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowFontSelector(false)}
                        className="text-rose-600 hover:text-rose-700 font-bold hover:underline cursor-pointer"
                      >
                        Cerrar panel
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Large Quote Emblem */}
            <div className="mt-6 mb-3 text-rose-400/50">
              <Quote className="w-10 h-10 rotate-180" />
            </div>

            {/* Phrase Typography Preview with Dynamic Font Family & Style */}
            <div className="my-2 sm:my-4 px-2 sm:px-6 w-full">
              <p
                style={{
                  fontFamily: activeFontItem.cssFamily,
                  fontStyle: activeFontItem.style,
                  fontWeight: activeFontItem.weight,
                }}
                className={`${getCardFontClass(activeFontItem)} text-center transition-all duration-300`}
              >
                "{currentPhrase.text}"
              </p>

              {currentPhrase.author && (
                <p className="mt-5 text-xs font-mono tracking-widest uppercase text-rose-600 font-bold text-center">
                  — {currentPhrase.author}
                </p>
              )}
            </div>

            {/* Card Action Controls */}
            <div className="mt-8 pt-6 border-t border-[#F0DDE4] w-full flex flex-col sm:flex-row items-center justify-center gap-3 text-xs">
              <button
                type="button"
                onClick={handleCopyQuote}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-white hover:bg-[#FFF4F7] text-[#302B30] border border-[#F0DDE4] font-bold transition-all cursor-pointer shadow-2xs"
              >
                {copiedQuote ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">¡Copiada para dedicar!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-[#77717A]" />
                    <span>Copiar Frase</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleNextQuote}
                disabled={isRefreshing}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold transition-all cursor-pointer shadow-md shadow-pink-500/25"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-white ${isRefreshing ? 'animate-spin' : ''}`} />
                <span>Siguiente Frase Mágica</span>
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ======================================================== */}
      {/* 3. MODAL: POSTAL ESTILO CUADERNO HD */}
      {/* ======================================================== */}
      <AnimatePresence>
        {showNoteModal && noteResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/35 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 15 }}
              className="relative w-full max-w-md bg-white rounded-3xl p-6 border border-[#F0DDE4] shadow-2xl flex flex-col items-center text-center space-y-4 max-h-[92vh] overflow-y-auto"
            >
              <div className="w-full flex items-center justify-between pb-3 border-b border-[#F0DDE4]">
                <div className="flex items-center gap-2.5 text-left">
                  <div className="w-9 h-9 rounded-xl bg-[#FFF4F7] border border-[#F0DDE4] flex items-center justify-center text-[#E83E78]">
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#302B30]">Postal de Amor</h3>
                    <p className="text-xs text-[#77717A]">Lista para guardar o compartir en HD</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowNoteModal(false)}
                  className="p-1.5 rounded-xl text-[#77717A] hover:text-[#302B30] hover:bg-[#FFF4F7]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Note Image Preview */}
              <div className="w-full rounded-2xl overflow-hidden border border-[#F0DDE4] shadow-sm">
                <img
                  src={noteResult.dataUrl}
                  alt="Postal de Frase Romántica"
                  className="w-full object-contain max-h-[380px]"
                />
              </div>

              {/* Actions */}
              <div className="w-full flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleDownloadImage}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                >
                  <Download className="w-4 h-4 text-[#E83E78]" />
                  <span>Descargar Imagen</span>
                </button>

                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-pink-500/25"
                >
                  <Share2 className="w-4 h-4 text-white" />
                  <span>Enviar / Compartir</span>
                </button>
              </div>

              {shareFeedbackMsg && (
                <div className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 py-1.5 px-3 rounded-lg w-full">
                  {shareFeedbackMsg}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
